<template>
  <div class="monitor-root" v-loader.opacity.tab="loading">
    <div class="monitor-stats">
      <div class="stat-card">
        <span class="stat-label">{{ $t('connector.sentToday') }}</span>
        <span class="stat-value">{{ stats.sentToday || 0 }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">{{ $t('connector.waiting') }}</span>
        <span class="stat-value">{{ stats.waiting || 0 }}</span>
      </div>
      <div class="stat-card" :class="{ 'has-error': (stats.error || 0) > 0 }">
        <span class="stat-label">{{ $t('connector.withError') }}</span>
        <span class="stat-value">{{ stats.error || 0 }}</span>
      </div>
    </div>

    <div class="monitor-table" v-if="tableJobs">
      <BaseTable class="no-border" :tabledata="tableJobs">
        <template v-slot:column_exam="{item}">
          <span class="raw-value">{{ item.examId }}</span>
        </template>
        <template v-slot:column_status="{item}">
          <span class="status-pill" :class="statusClass(item.statusRaw)">{{ statusLabel(item.statusRaw) }}</span>
          <span class="status-pill permanent-pill" v-if="isPermanent(item)"
                :data-title="item.errorMessage || $t('connector.errorPermanentHint')" data-test="pillPermanentError">
            {{ $t('connector.errorPermanent') }}
          </span>
        </template>
      </BaseTable>
    </div>

    <div class="action-bar">
      <button class="btn btn-outline-secondary" type="button" @click="fetchData" data-test="btnRefreshJobs">
        <i class="fa fa-refresh"></i>
        <span>{{ $t('connector.refresh') }}</span>
      </button>
      <button class="btn btn-outline-danger" type="button" :disabled="!hasErrors" @click="retryErrors" data-test="btnRetryErrors">
        <i class="fa fa-redo"></i>
        <span>{{ $t('connector.retryErrors') }} ({{ transientErrorJobs.length }})</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.monitor-stats {
  display:                      flex;
  gap:                          14px;
  padding:                      4px 10px 14px;
}

.stat-card {
  flex:                         1;
  display:                      flex;
  flex-direction:               column;
  gap:                          6px;
  padding:                      14px 16px;
  border:                       1px solid var(--Shades-30-Border);
  border-radius:                8px;
  background:                   var(--Shades-10);

  &.has-error {
    border-color:               var(--Support-Error);

    .stat-value {
      color:                    var(--Support-Error);
    }
  }

  .stat-label {
    font-size:                  11px;
    letter-spacing:             1.2px;
    text-transform:             uppercase;
    color:                      var(--Shades-60-W);
  }

  .stat-value {
    font-size:                  28px;
    font-weight:                600;
    color:                      var(--Brand-Primary);
  }
}

.monitor-table {
  padding:                      0 10px;
}

.raw-value {
  text-transform:               none;
}

.status-pill {
  display:                      inline-block;
  font-size:                    11px;
  font-weight:                  600;
  letter-spacing:               0.8px;
  text-transform:               uppercase;
  padding:                      4px 12px;
  border-radius:                10px;

  &.completed {
    background:                 var(--Brand-Secondary, #3CB391);
    color:                      #FFF;
  }

  &.failed {
    background:                 var(--Support-Error, #EE4930);
    color:                      #FFF;
  }

  &.progress {
    background:                 var(--Complementary-Yellow, #F3CD90);
    color:                      #232323;
  }
}

.permanent-pill {
  margin-left:                  6px;
  cursor:                       help;
  background:                   transparent;
  border:                       1px solid var(--Support-Error, #EE4930);
  color:                        var(--Support-Error, #EE4930);
}

.action-bar {
  display:                      flex;
  gap:                          10px;
  padding:                      14px 10px;

  .btn {
    gap:                        8px;
  }

  .btn-outline-secondary i {
    color:                      var(--Brand-Secondary, #3CB391);
  }
}

@media (min-width: 992px) {
  .monitor-root,
  .monitor-table {
    display:                    flex;
    flex-direction:             column;
    flex:                       1;
    min-height:                 0;
  }
}

@media (max-width: 991px) {
  .monitor-table {
    height:                     calc(100dvh - 360px);
    max-height:                 calc(100dvh - 360px);
    overflow-y:                 auto;
    padding:                    0;
  }
}
</style>

<script>
import { partnerLabel } from '@/services/notification-labels'

export default {
  name: 'ClinicConnectorMonitor',
  props: ['clinic_uuid'],
  data: () => ({
    loading: false,
    jobs: [],
    stats: {},
    tableJobs: null,
  }),
  computed: {
    transientErrorJobs() {
      return this.jobs.filter(j => j.statusRaw === 'error' && j.errorKind !== 'permanent')
    },
    hasErrors() {
      return this.transientErrorJobs.length > 0
    },
  },
  mounted() {
    this.fetchData()
    this.onNotification = (event) => {
      if (event && event.connector) this.fetchData()
    }
    this.emitter.on('newNotification', this.onNotification)
  },
  beforeUnmount() {
    if (this.onNotification) this.emitter.off('newNotification', this.onNotification)
  },
  methods: {
    fetchData: async function() {
      if (!this.clinic_uuid) return
      this.loading = true
      NProgress.start()
      try {
        let response = await this.$connectorService.listJobs({ clinicId: this.clinic_uuid })
        let data = response && response.result ? response.result : response
        this.stats = data.stats || {}
        this.jobs = (data.jobs || []).map(j => ({
          partnerLabel: partnerLabel(j.partner),
          examId: j.examId || '—',
          laterality: j.laterality || '—',
          analysis: j.analysis,
          statusRaw: j.status,
          errorKind: j.errorKind || null,
          errorMessage: j.error || '',
          imageId: j.imageId || '—',
          updatedAt: j.updatedAt,
          idempotencyKey: j.idempotencyKey,
        }))
        this.tableJobs = {
          'items': this.jobs,
          'columns': [
            {name: this.$i18n.t('connector.partner'), type: 'string', value: 'partnerLabel', width: '13%'},
            {name: this.$i18n.t('connector.exam'), type: 'custom', key: 'exam', width: '20%'},
            {name: this.$i18n.t('connector.laterality'), type: 'string', value: 'laterality', width: '8%'},
            {name: this.$i18n.t('connector.analysis'), type: 'string', value: 'analysis', width: '10%'},
            {name: this.$i18n.t('integration.statusCol'), type: 'custom', key: 'status', width: '21%'},
            {name: this.$i18n.t('connector.image'), type: 'string', value: 'imageId', width: '13%'},
            {name: this.$i18n.t('connector.updatedAt'), type: 'date', value: 'updatedAt', width: '15%'},
          ]
        }
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    statusClass(status) {
      if (status === 'completed') return 'completed'
      if (status === 'error') return 'failed'
      return 'progress'
    },
    statusLabel(status) {
      return this.$i18n.t(`connector.jobStatus.${status}`)
    },
    isPermanent(item) {
      return item.statusRaw === 'error' && item.errorKind === 'permanent'
    },
    retryErrors: async function() {
      const keys = this.transientErrorJobs.map(j => j.idempotencyKey)
      if (!keys.length) return
      this.loading = true
      NProgress.start()
      try {
        const response = await this.$connectorService.retryJobs({ clinicId: this.clinic_uuid, idempotencyKeys: keys })
        const data = response && response.result ? response.result : (response || {})
        const rejected = Array.isArray(data.rejected) ? data.rejected : []
        const accepted = data.accepted !== undefined ? data.accepted : keys.length

        if (rejected.length) {
          this.$message.popup(this, 'connector.retryPartial', 'operationWarning', 'warn', { params: { accepted, rejected: rejected.length } })
        } else {
          this.$message.popup(this, 'connector.retryStarted', 'operationSuccess', 'success')
        }
        setTimeout(() => this.fetchData(), 1500)
      } catch (err) {
        console.log(err)
        const status = err && err.response ? err.response.status : null
        this.$message.popup(this, status === 422 ? 'connector.retryAllPermanent' : 'connector.retryFail', 'operationFail', 'error')
        if (status === 422) setTimeout(() => this.fetchData(), 500)
      }
      NProgress.done(true)
      this.loading = false
    },
  },
}
</script>
